import { useDisclosure, useToast } from '@chakra-ui/react'
import type React from 'react'
import { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import PageWrapper from '../../../../../components/Pagewrapper'
import Toast from '../../../../../components/Toast'
import {
  useActivateWealthManagerMutation,
  useGetVeliActivationFunnelQuery,
  useGetWealthManagersQuery,
  useInviteWealthManagerMutation,
  useRemindVeliBookMutation,
} from '../../../../../redux/services/coreApi'
import type { PageTab } from '../../../../../types/internal-types'
import ConfirmDialog from './components/ConfirmDialog'
import RiskRatingModal from './components/RiskRatingModal'
import VeliClientsTable, {
  type VeliClientRow,
} from './components/VeliClientsTable'
import WealthManagerOverview from './components/WealthManagerOverview'

// Pull a server-supplied error message out of an RTK Query error (the API's RestException body), falling
// back to a generic line so a 400 (already invited / email collision) reads usefully in the toast.
const errorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { data?: unknown })?.data
  if (typeof data === 'string' && data.length > 0) return data
  if (
    data &&
    typeof data === 'object' &&
    'message' in data &&
    typeof (data as { message?: unknown }).message === 'string'
  ) {
    return (data as { message: string }).message
  }
  return fallback
}

// A single wealth manager's detail page — the Clients-style drill-in. Identity/status on the Overview
// tab, their book on the Clients tab, and the lifecycle actions (invite, mark agreement signed, set risk
// rating) as page-header actions rather than per-row table bullets. Data is reused from the existing
// list + funnel endpoints; there is no dedicated single-WM endpoint.
const WealthManagerScreen: React.FC = () => {
  const history = useHistory()
  const toast = useToast()
  const { wealthManagerId } = useParams<{ wealthManagerId: string }>()

  const { data, isLoading } = useGetWealthManagersQuery()
  const wealthManager = data?.wealthManagers.find(
    wm => wm.id === wealthManagerId
  )

  const { data: funnel, isLoading: isLoadingFunnel } =
    useGetVeliActivationFunnelQuery({ wealthManagerId })
  const book = funnel?.wealthManagers[0]

  const [invite, { isLoading: inviteIsLoading }] =
    useInviteWealthManagerMutation()
  const [activate, { isLoading: activateIsLoading }] =
    useActivateWealthManagerMutation()
  const [remindBook, { isLoading: remindIsLoading }] =
    useRemindVeliBookMutation()

  const inviteDialog = useDisclosure()
  const activateDialog = useDisclosure()
  const remindDialog = useDisclosure()
  const riskModal = useDisclosure()

  const [tab, setTab] = useState<'overview' | 'clients'>('overview')

  const displayName = wealthManager
    ? [wealthManager.firstName, wealthManager.lastName]
        .filter(Boolean)
        .join(' ') || wealthManager.email
    : ''

  const onConfirmInvite = async () => {
    if (!wealthManager) return
    try {
      await invite(wealthManager.id).unwrap()
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="success">Invitation sent to {displayName}.</Toast>
        ),
      })
      inviteDialog.onClose()
    } catch (error) {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">
            {errorMessage(error, 'Failed to invite the wealth manager.')}
          </Toast>
        ),
      })
    }
  }

  const onConfirmActivate = async () => {
    if (!wealthManager) return
    try {
      await activate(wealthManager.id).unwrap()
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="success">
            Agreement marked signed for {displayName}.
          </Toast>
        ),
      })
      activateDialog.onClose()
    } catch (error) {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">
            {errorMessage(error, 'Failed to mark the agreement signed.')}
          </Toast>
        ),
      })
    }
  }

  const onConfirmRemind = async () => {
    if (!wealthManager) return
    try {
      const result = await remindBook(wealthManager.id).unwrap()
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="success">
            Reminders sent to {result.sent} client
            {result.sent === 1 ? '' : 's'}.
          </Toast>
        ),
      })
      remindDialog.onClose()
    } catch {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">Failed to send reminders. Please try again.</Toast>
        ),
      })
    }
  }

  const tabs: PageTab[] = [
    {
      title: 'Overview',
      active: tab === 'overview',
      onPress: () => setTab('overview'),
    },
    {
      title: 'Clients',
      active: tab === 'clients',
      onPress: () => setTab('clients'),
      amount: book ? `${book.total}` : `${wealthManager?.clientCount ?? 0}`,
    },
  ]

  const clientRows: VeliClientRow[] =
    book?.clients.map(client => ({ ...client, id: client.veliUserId })) ?? []

  const notActivated = book?.notActivated ?? 0

  return (
    <>
      <PageWrapper
        title={displayName}
        goBack={() => history.goBack()}
        isLoading={isLoading || isLoadingFunnel}
        tabs={tabs}
      >
        {!wealthManager && !isLoading ? (
          <p className="font-semi color-text-description py-10 text-center">
            Wealth manager not found.
          </p>
        ) : tab === 'overview' ? (
          wealthManager && (
            <WealthManagerOverview
              wealthManager={wealthManager}
              book={book}
              onInvite={inviteDialog.onOpen}
              onMarkAgreementSigned={activateDialog.onOpen}
              onSetRiskRating={riskModal.onOpen}
            />
          )
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-end">
              <button
                type="button"
                className="button-secondary"
                disabled={notActivated === 0}
                onClick={remindDialog.onOpen}
              >
                Remind not-activated clients
              </button>
            </div>
            <VeliClientsTable
              clients={clientRows}
              isLoading={isLoadingFunnel}
              showWealthManager={false}
            />
          </div>
        )}
      </PageWrapper>

      <ConfirmDialog
        isOpen={inviteDialog.isOpen}
        onClose={inviteDialog.onClose}
        onConfirm={onConfirmInvite}
        title="Invite wealth manager"
        confirmLabel="Invite"
        isLoading={inviteIsLoading}
      >
        {wealthManager && (
          <>
            <p>
              Mint an Auth0 login for{' '}
              <span className="color-text-default font-semi">{displayName}</span>{' '}
              and email them a set-password link at{' '}
              <span className="color-text-default font-semi">
                {wealthManager.email}
              </span>
              ?
            </p>
            <p>They can sign in to Penning once they set a password.</p>
          </>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={activateDialog.isOpen}
        onClose={activateDialog.onClose}
        onConfirm={onConfirmActivate}
        title="Mark agreement signed"
        confirmLabel="Mark agreement signed"
        isLoading={activateIsLoading}
      >
        {wealthManager && (
          <>
            <p>
              Confirm that the out-of-band agreement with{' '}
              <span className="color-text-default font-semi">{displayName}</span>{' '}
              is signed.
            </p>
            <p>
              This grants the wealth manager compliance visibility into their own
              book of clients. It is not reversible — there is no un-activate, and
              re-doing it keeps the first timestamp.
            </p>
          </>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={remindDialog.isOpen}
        onClose={remindDialog.onClose}
        onConfirm={onConfirmRemind}
        title="Remind not-activated clients"
        confirmLabel="Send reminders"
        isLoading={remindIsLoading}
        isConfirmDisabled={notActivated === 0}
      >
        <p>
          Email the activation link to the{' '}
          <span className="color-text-default font-semi">{notActivated}</span>{' '}
          not-yet-activated client{notActivated === 1 ? '' : 's'} in{' '}
          <span className="color-text-default font-semi">{displayName}</span>'s
          book. Already-activated clients are never re-contacted.
        </p>
      </ConfirmDialog>

      <RiskRatingModal
        isOpen={riskModal.isOpen}
        onClose={riskModal.onClose}
        wealthManager={wealthManager}
      />
    </>
  )
}

export default WealthManagerScreen
